import { useLazyUser } from "$global-providers/user-provider";
import { gql } from "@apollo/client";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { EpocusConsentDocument } from "../collections/epocus-consent-document";
import type { RxDBCollections } from "../provider";
import {
  SyncEpocusConsentDocumentPullDocument,
  type WholeSyncableEpocusConsentDocumentFragment,
} from "./__generated__/epocus-consent-document.gql";
import {
  EPOCUS_CONSENT_REPLICATION_ROLES,
  isReplicationRoleAllowed,
} from "./replication-role-allowlist";
import { useGqlReplication } from "./use-gql-replication";

export const useReplicateEpocusConsentDocument = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<EpocusConsentDocument>;
}) => {
  const role = useLazyUser()?.role;
  const roleAllowed = isReplicationRoleAllowed(
    EPOCUS_CONSENT_REPLICATION_ROLES,
    role,
  );

  const pull = useMemo(
    () => ({
      query: SyncEpocusConsentDocumentPullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: { limit: batchSize, checkpoint },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { hours: 1 },
    }),
    [],
  );

  return useGqlReplication<
    EpocusConsentDocument,
    WholeSyncableEpocusConsentDocumentFragment,
    null
  >({
    db,
    collection,
    pull: roleAllowed ? pull : undefined,
    mapGqlToRx: mapGqlEpocusConsentDocumentToRx,
    batchSize: 1000,
    metadataLocalStorageKeySuffix: "epocus-consent-document",
    replicationIdentifierSuffix: "audience",
  });
};

export const mapGqlEpocusConsentDocumentToRx = (
  doc: WholeSyncableEpocusConsentDocumentFragment,
): WithDeleted<EpocusConsentDocument> => ({
  id: doc.id,
  version: doc.version,
  items: doc.items.map((item) => ({
    id: item.id,
    audience:
      item.audience as EpocusConsentDocument["items"][number]["audience"],
    kind: item.kind as EpocusConsentDocument["items"][number]["kind"],
    ordinal: item.ordinal,
    bodyText: item.bodyText,
  })),
  meta: {
    createdAt: doc.meta.createdAt,
    updatedAt: doc.meta.updatedAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedByUserId: doc.meta.updatedByUserId,
  },
  _deleted: doc.meta.deleted,
});

gql`
  fragment WholeSyncableEpocusConsentDocument on SyncableEpocusConsentDocument {
    id
    version
    items {
      id
      audience
      kind
      ordinal
      bodyText
    }
    meta {
      createdAt
      updatedAt
      createdByUserId
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncEpocusConsentDocumentPull($input: PullEpocusConsentDocumentInput!) {
    pullEpocusConsentDocument(input: $input) {
      documents {
        ...WholeSyncableEpocusConsentDocument
      }
    }
  }
`;
